import { useState, useEffect, useCallback } from 'react';
import { useConnector } from '../contexts/ConnectorContext';
import type { Order } from '../types/ordering.types';
import { OrderStatus, isOverdue } from '../types/ordering.types';

interface OrdersState {
  orders: Order[];
  isLoading: boolean;
  error: Error | null;
}

export function useMyOrders(userEmail: string) {
  const connector = useConnector();
  const [state, setState] = useState<OrdersState>({ orders: [], isLoading: true, error: null });

  const load = useCallback(() => {
    if (!userEmail) return;
    setState(s => ({ ...s, isLoading: true, error: null }));
    connector
      .getOrders({ requesterEmail: userEmail })
      .then(orders => setState({ orders, isLoading: false, error: null }))
      .catch(err => setState({ orders: [], isLoading: false, error: err instanceof Error ? err : new Error(String(err)) }));
  }, [connector, userEmail]);

  useEffect(() => { load(); }, [load]);

  return { ...state, reload: load };
}

export function useAllOrders() {
  const connector = useConnector();
  const [state, setState] = useState<OrdersState>({ orders: [], isLoading: true, error: null });

  const load = useCallback(() => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    connector
      .getOrders()
      .then(orders => {
        // Sort: overdue first (by expected delivery date asc), then non-overdue by expected date asc, then no date last
        const sorted = [...orders].sort((a, b) => {
          const aOver = isOverdue(a);
          const bOver = isOverdue(b);
          if (aOver && !bOver) return -1;
          if (!aOver && bOver) return 1;
          const aDate = a.expectedDeliveryDate ?? '';
          const bDate = b.expectedDeliveryDate ?? '';
          if (!aDate && bDate) return 1;
          if (aDate && !bDate) return -1;
          return aDate < bDate ? -1 : aDate > bDate ? 1 : 0;
        });
        setState({ orders: sorted, isLoading: false, error: null });
      })
      .catch(err => setState({ orders: [], isLoading: false, error: err instanceof Error ? err : new Error(String(err)) }));
  }, [connector]);

  useEffect(() => { load(); }, [load]);

  return { ...state, reload: load };
}

export function useFYIOrders(userEmail: string) {
  const connector = useConnector();
  const [state, setState] = useState<OrdersState>({ orders: [], isLoading: true, error: null });

  const load = useCallback(() => {
    if (!userEmail) return;
    setState(s => ({ ...s, isLoading: true, error: null }));
    connector
      .getOrders({ fyiViewerEmail: userEmail })
      .then(orders => setState({ orders, isLoading: false, error: null }))
      .catch(err => setState({ orders: [], isLoading: false, error: err instanceof Error ? err : new Error(String(err)) }));
  }, [connector, userEmail]);

  useEffect(() => { load(); }, [load]);

  return { ...state, reload: load };
}

export interface PendingApprovalsOptions {
  isCFO?: boolean;
  isChemicalApprover?: boolean;
  isOMGroup?: boolean;
  isOBOCEO?: boolean;
}

export function usePendingApprovals(
  userEmail: string,
  purchaseApprovalLimit: number | null,
  options: PendingApprovalsOptions = {},
) {
  const connector = useConnector();
  const [state, setState] = useState<OrdersState>({ orders: [], isLoading: true, error: null });

  const load = useCallback(() => {
    if (!userEmail) return;
    setState(s => ({ ...s, isLoading: true, error: null }));
    Promise.all([connector.getOrders(), connector.getPeople({ active: true })])
      .then(([orders, people]) => {
        const email = userEmail.trim().toLowerCase();
        // Build lookup: requester email → current Person (for live LM/role resolution)
        const peopleByEmail = new Map(people.map(p => [p.m365Email.trim().toLowerCase(), p]));

        // Derive role flags from the live people list so changes take effect immediately
        const currentUserLive = peopleByEmail.get(email);
        const liveIsCFO          = currentUserLive?.isCFO                    ?? (options.isCFO              ?? false);
        const liveIsChemApprover = currentUserLive?.isChemicalApprover       ?? (options.isChemicalApprover ?? false);
        const liveIsOMGroup      = currentUserLive?.isOrderMgmtGroup         ?? (options.isOMGroup          ?? false);
        const liveIsOBOCEO       = currentUserLive?.canApproveOnBehalfOfCEO  ?? (options.isOBOCEO           ?? false);

        const DONE_STATUSES = new Set([
          OrderStatus.Ordered, OrderStatus.PartialDelivery, OrderStatus.Delivered,
          OrderStatus.ForReview, OrderStatus.Rejected, OrderStatus.Cancelled,
          OrderStatus.Draft,
        ]);

        const pending = orders.filter(o => {
          // Never show orders that are past the approval/processing gate
          if (DONE_STATUSES.has(o.status)) return false;

          const sv = o.sterlingEquivalent ?? o.totalAmount;
          // Line-manager stage (chemical is now first — Requested here means LM approval needed) (also covers escalated approver when LM's limit was insufficient)
          if (o.status === OrderStatus.Requested) {
            // Never show to the requester themselves
            if (o.requesterEmail.trim().toLowerCase() === email) return false;
            const requester = peopleByEmail.get(o.requesterEmail.trim().toLowerCase());
            const currentLMEmail = (requester?.lineManagerEmail?.trim() ?? o.lineManagerEmail?.trim() ?? '').toLowerCase();
            // If escalatedApproverEmail is set the LM already approved — only show to the escalated approver
            if (o.escalatedApproverEmail) {
              return o.escalatedApproverEmail.trim().toLowerCase() === email;
            }
            return currentLMEmail === email;
          }

          // Escalated approval — LM approved but their limit was insufficient; walk hierarchy
          if (o.status === OrderStatus.PendingEscalatedApproval) {
            const lmEmail = o.lineManagerEmail?.toLowerCase().trim();
            if (!lmEmail) return false;
            const lmPerson = peopleByEmail.get(lmEmail);
            if (!lmPerson) return false;
            const visited = new Set<string>([lmEmail]);
            let cur = lmPerson.lineManagerEmail?.toLowerCase().trim() ?? '';
            for (let depth = 0; depth < 20; depth++) {
              if (!cur || visited.has(cur)) break;
              visited.add(cur);
              const person = peopleByEmail.get(cur);
              if (!person) break;
              const limit = person.purchaseApprovalLimit ?? Infinity;
              if (sv <= limit) return cur === email;
              cur = person.lineManagerEmail?.toLowerCase().trim() ?? '';
            }
            return false;
          }

          // CEO On-Behalf-Of stage — shown to anyone with canApproveOnBehalfOfCEO
          if (o.status === OrderStatus.PendingCEOApproval)
            return liveIsOBOCEO && !o.approvedOnBehalfOf;

          // CFO approval stage
          if (o.status === OrderStatus.PendingCFOApproval)
            return liveIsCFO && !o.cfoApproverEmail;

          // Chemical approver — now first in the chain, shown as soon as order is submitted
          if (o.status === OrderStatus.PendingChemApproval || o.status === OrderStatus.LMApproved) {
            return liveIsChemApprover && o.hasChemicalLines;
          }

          // OM group processing queue
          if (o.status === OrderStatus.OMApproved || o.status === OrderStatus.ChemicalApproved)
            return liveIsOMGroup;

          return false;
        });
        setState({ orders: pending, isLoading: false, error: null });
      })
      .catch(err => setState({ orders: [], isLoading: false, error: err instanceof Error ? err : new Error(String(err)) }));
  }, [connector, userEmail, purchaseApprovalLimit, options.isCFO, options.isChemicalApprover, options.isOMGroup, options.isOBOCEO]);

  useEffect(() => { load(); }, [load]);

  return { ...state, reload: load };
}

// Orders where the current user has already acted as an approver.
// Role flags are derived from the live people list on every call.
export function useReviewedOrders(userEmail: string) {
  const connector = useConnector();
  const [state, setState] = useState<OrdersState>({ orders: [], isLoading: true, error: null });

  const load = useCallback(() => {
    if (!userEmail) return;
    setState(s => ({ ...s, isLoading: true, error: null }));
    Promise.all([connector.getOrders(), connector.getPeople({ active: true })])
      .then(([orders, people]) => {
        const email = userEmail.toLowerCase();
        const currentUserLive = people.find(p => p.m365Email.toLowerCase() === email);
        const liveIsChemApprover = currentUserLive?.isChemicalApprover ?? false;
        const liveIsOMGroup      = currentUserLive?.isOrderMgmtGroup   ?? false;

        // Chemical is now first in the chain, so all statuses after PendingChemApproval count as "past"
        const PAST_CHEMICAL: OrderStatus[] = [
          OrderStatus.Requested, OrderStatus.PendingEscalatedApproval,
          OrderStatus.PendingCEOApproval, OrderStatus.PendingCFOApproval,
          OrderStatus.ChemicalApproved, OrderStatus.OMApproved,
          OrderStatus.Ordered, OrderStatus.PartialDelivery, OrderStatus.Delivered,
          OrderStatus.ForReview,
        ];
        const OM_DONE: OrderStatus[] = [
          OrderStatus.Ordered, OrderStatus.PartialDelivery, OrderStatus.Delivered,
        ];

        const reviewed = orders.filter(o => {
          if (o.status === OrderStatus.Draft) return false;

          // Acted as Line Manager (stored at submission): order moved past Requested
          if (o.lineManagerEmail.toLowerCase() === email && o.status !== OrderStatus.Requested) return true;

          // Acted as CFO: email recorded on order
          if (o.cfoApproverEmail && o.cfoApproverEmail.toLowerCase() === email) return true;

          // Acted as Chemical Approver: chemical order now past chemical gate
          if (liveIsChemApprover && o.hasChemicalLines && PAST_CHEMICAL.includes(o.status)) return true;

          // Acted as OM Group: order has been actioned (marked ordered / delivered)
          if (liveIsOMGroup && OM_DONE.includes(o.status)) return true;

          return false;
        });

        // Most recent first
        reviewed.sort((a, b) => {
          const ad = a.orderDate ?? '';
          const bd = b.orderDate ?? '';
          return bd < ad ? -1 : bd > ad ? 1 : 0;
        });

        setState({ orders: reviewed, isLoading: false, error: null });
      })
      .catch(err => setState({ orders: [], isLoading: false, error: err instanceof Error ? err : new Error(String(err)) }));
  }, [connector, userEmail]);

  useEffect(() => { load(); }, [load]);

  return { ...state, reload: load };
}
